import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Send, ArrowRightCircle } from "lucide-react";
import { api } from "@/lib/api";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const PROCUREMENT_WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT"];
const EMPTY_ITEM = { description: "", quantity: "", estimated_rate: "" };

export default function PurchaseIndents() {
  const pf = useProjectFilter();
  const { user } = useAuth();
  const canWrite = PROCUREMENT_WRITE_ROLES.includes(user?.role);
  const { companies } = useCompany() || {};
  const [indents, setIndents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/procurement/indents");
      setIndents(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || "—";

  const submit = async (indent) => {
    setBusyId(indent.id);
    try {
      const { data } = await api.post(`/procurement/indents/${indent.id}/submit`);
      toast.success(data?.status === "CONVERTED"
        ? "Indent approved — a Purchase Requisition was created automatically"
        : "Indent submitted — once approved, its Purchase Requisition is created automatically");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const convert = async (indent) => {
    setBusyId(indent.id);
    try {
      await api.post(`/procurement/indents/${indent.id}/convert-to-requisition`, { requisition_date: new Date().toISOString().slice(0, 10) });
      toast.success("Converted to purchase requisition");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="purchase-indents-page">
      <PageHeader title="Purchase Indents" description="Internal requests to purchase — the first step of the procure-to-pay chain." action={<div className="flex flex-wrap items-center justify-end gap-2"><ProjectFilter filter={pf} />{canWrite && <CreateIndentDialog companies={companies} pfProjects={pf.projects} onCreated={load} />}</div>} />
      {!loading && indents.length === 0 ? (
        <EmptyState title="No purchase indents yet" testId="indents-empty-state" />
      ) : (
        <div className="space-y-3">
          {pf.apply(indents, (r) => r.project_id).map((indent) => (
            <div key={indent.id} className="rounded-xl border border-border bg-card p-4" data-testid={`indent-card-${indent.number}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-xs text-muted-foreground">{indent.number}</p>
                  <p className="text-sm text-foreground">{companyName(indent.company_id)} · {indent.indent_date}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill value={indent.status} />
                  {canWrite && indent.status === "DRAFT" && (
                    <Button size="sm" variant="outline" disabled={busyId === indent.id} onClick={() => submit(indent)} data-testid={`submit-indent-button-${indent.number}`}>
                      <Send className="h-3.5 w-3.5" /> Submit
                    </Button>
                  )}
                  {canWrite && indent.status === "APPROVED" && (
                    <Button size="sm" variant="outline" disabled={busyId === indent.id} onClick={() => convert(indent)} data-testid={`convert-indent-button-${indent.number}`}>
                      <ArrowRightCircle className="h-3.5 w-3.5" /> Convert to PR
                    </Button>
                  )}
                </div>
              </div>
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                {indent.items.map((item) => (
                  <li key={item.id}>{item.description} — {item.quantity} @ {item.estimated_rate || "—"}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateIndentDialog({ companies, pfProjects, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", indent_date: "", remarks: "" });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateItem = (idx, field, value) => setItems(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/procurement/indents", {
        ...form, project_id: form.project_id || null,
        items: items.map((it) => ({ ...it, estimated_rate: it.estimated_rate || null })),
      });
      toast.success("Purchase indent created");
      setForm({ company_id: "", indent_date: "", remarks: "" });
      setItems([{ ...EMPTY_ITEM }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-indent-button"><Plus className="h-4 w-4" /> New Indent</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Purchase Indent</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-indent-form">
          <div>
            <Label>Project <span className="font-normal text-muted-foreground">(the site this material is for)</span></Label>
            <Select value={form.project_id || ""} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="indent-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>{(pfProjects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="indent-company-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" data-testid="indent-date-input" value={form.indent_date} onChange={set("indent_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Items</Label>
            <div className="mt-1.5 space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`indent-item-row-${idx}`}>
                  <Input required placeholder="Description" value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} data-testid={`indent-item-desc-input-${idx}`} />
                  <Input required type="number" step="0.001" placeholder="Qty" className="w-24" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} data-testid={`indent-item-qty-input-${idx}`} />
                  <Input type="number" step="0.01" placeholder="Est. rate" className="w-28" value={it.estimated_rate} onChange={(e) => updateItem(idx, "estimated_rate", e.target.value)} data-testid={`indent-item-rate-input-${idx}`} />
                  {items.length > 1 && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== idx))} data-testid={`remove-indent-item-button-${idx}`}>
                      <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setItems([...items, { ...EMPTY_ITEM }])} data-testid="add-indent-item-button">
              <Plus className="h-3.5 w-3.5" /> Add item
            </Button>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-indent-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
